import React, { useState } from "react";
import { Star, Heart, Home, ThumbsUp, ThumbsDown, X, MessageSquare, Shield, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITIVE_TAGS = [
  { code: "friendly", label: "Friendly & welcoming", emoji: "😊" },
  { code: "punctual", label: "On time", emoji: "⏰" },
  { code: "great_kids", label: "Kids got along great", emoji: "👫" },
  { code: "good_communication", label: "Good communication", emoji: "💬" },
  { code: "clean_tidy", label: "Clean & tidy", emoji: "✨" },
  { code: "respectful", label: "Respectful", emoji: "🤝" },
  { code: "fun_activities", label: "Fun activities", emoji: "🎨" },
  { code: "would_recommend", label: "Would recommend", emoji: "⭐" },
];

const CONSTRUCTIVE_TAGS = [
  { code: "late", label: "Running late", emoji: "🕐" },
  { code: "communication_issues", label: "Communication issues", emoji: "📱" },
  { code: "supervision", label: "Supervision concerns", emoji: "👀" },
  { code: "not_as_described", label: "Not as described", emoji: "❓" },
];

const TimeToSayGoodbye = ({ meetupId, otherFamilyName, onComplete, onClose }) => {
  const [step, setStep] = useState(1);
  const [rating, setRating] = useState(0);
  const [wouldMeetAgain, setWouldMeetAgain] = useState(null);
  const [homeSafe, setHomeSafe] = useState(false);
  const [favourite, setFavourite] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [privateNotes, setPrivateNotes] = useState("");
  const [anonymousFeedback, setAnonymousFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggleTag = (code) => {
    if (selectedTags.includes(code)) {
      setSelectedTags(selectedTags.filter(t => t !== code));
    } else {
      setSelectedTags([...selectedTags, code]);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please rate your experience");
      return;
    }
    if (wouldMeetAgain === null) {
      toast.error("Please indicate if you would meet again");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/meetups/${meetupId}/review`, {
        rating,
        would_meet_again: wouldMeetAgain,
        home_safe_confirmed: homeSafe,
        favourite_family: favourite,
        private_notes: privateNotes,
        anonymous_feedback: anonymousFeedback,
        feedback_tags: selectedTags
      });
      
      toast.success("Thanks for your feedback! Safe travels home 🏠");
      if (onComplete) onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-500 to-pink-500 p-6 text-white rounded-t-2xl">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Time to Say Goodbye!
              </h2>
              <p className="text-white/80 text-sm">How was your playdate with {otherFamilyName}?</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Step 1: Rating */}
          {step >= 1 && (
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-slate-700">
                Rate your experience
              </label>
              <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => { setRating(star); if (step === 1) setStep(2); }}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`w-10 h-10 ${rating >= star ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-sm text-slate-500">
                  {rating === 1 && "Not great 😕"}
                  {rating === 2 && "Could be better"}
                  {rating === 3 && "It was okay"}
                  {rating === 4 && "Good playdate! 😊"}
                  {rating === 5 && "Amazing! Best playdate ever! 🎉"}
                </p>
              )}
            </div>
          )}

          {/* Step 2: Would Meet Again */}
          {step >= 2 && (
            <div className="space-y-3 animate-fade-in">
              <label className="block text-sm font-semibold text-slate-700">
                Would you meet this family again?
              </label>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => { setWouldMeetAgain(true); if (step === 2) setStep(3); }}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                    wouldMeetAgain === true 
                      ? 'bg-green-500 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-green-100'
                  }`}
                  data-testid="would-meet-yes"
                >
                  <ThumbsUp className="w-5 h-5" />
                  Yes!
                </button>
                <button
                  onClick={() => { setWouldMeetAgain(false); if (step === 2) setStep(3); }}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                    wouldMeetAgain === false 
                      ? 'bg-rose-500 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-rose-100'
                  }`}
                  data-testid="would-meet-no"
                >
                  <ThumbsDown className="w-5 h-5" />
                  No
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Quick Tags */}
          {step >= 3 && (
            <div className="space-y-3 animate-fade-in">
              <label className="block text-sm font-semibold text-slate-700">
                Quick feedback (select all that apply)
              </label>
              <div className="space-y-2">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Positives</p>
                <div className="flex flex-wrap gap-2">
                  {POSITIVE_TAGS.map((tag) => (
                    <button
                      key={tag.code}
                      onClick={() => toggleTag(tag.code)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedTags.includes(tag.code)
                          ? 'bg-green-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {tag.emoji} {tag.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mt-3">Areas for improvement</p>
                <div className="flex flex-wrap gap-2">
                  {CONSTRUCTIVE_TAGS.map((tag) => (
                    <button
                      key={tag.code}
                      onClick={() => toggleTag(tag.code)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedTags.includes(tag.code)
                          ? 'bg-amber-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {tag.emoji} {tag.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Notes & Feedback */}
          {step >= 3 && (
            <div className="space-y-4 animate-fade-in">
              {/* Anonymous Feedback */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Anonymous feedback for {otherFamilyName}
                </label>
                <Textarea
                  value={anonymousFeedback}
                  onChange={(e) => setAnonymousFeedback(e.target.value)}
                  placeholder="They'll see this but won't know it's from you..."
                  className="rounded-xl"
                  rows={2}
                  maxLength={300}
                />
                <p className="text-xs text-slate-400 mt-1">They can see this feedback but not who wrote it</p>
              </div>

              {/* Private Notes */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Private notes (only you can see)
                </label>
                <Textarea
                  value={privateNotes}
                  onChange={(e) => setPrivateNotes(e.target.value)}
                  placeholder="Personal reminders about this family..."
                  className="rounded-xl"
                  rows={2}
                  maxLength={500}
                />
                <p className="text-xs text-slate-400 mt-1">Only visible to you - for your reference</p>
              </div>
            </div>
          )}

          {/* Step 5: Home Safe & Favourite */}
          {step >= 3 && (
            <div className="space-y-3 pt-4 border-t border-slate-100 animate-fade-in">
              {/* Home Safe */}
              <label className="flex items-center gap-3 p-3 bg-teal-50 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={homeSafe}
                  onChange={(e) => setHomeSafe(e.target.checked)}
                  className="w-5 h-5 rounded text-teal-500"
                />
                <div>
                  <span className="font-medium text-teal-800 flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    We got home safely
                  </span>
                  <span className="text-xs text-teal-600 block">Let us know you're safe</span>
                </div>
              </label>

              {/* Favourite Family */}
              <label className="flex items-center gap-3 p-3 bg-pink-50 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={favourite}
                  onChange={(e) => setFavourite(e.target.checked)}
                  className="w-5 h-5 rounded text-pink-500"
                />
                <div>
                  <span className="font-medium text-pink-800 flex items-center gap-2">
                    <Heart className="w-4 h-4" fill={favourite ? "#EC4899" : "none"} />
                    Add to favourites
                  </span>
                  <span className="text-xs text-pink-600 block">Save this family for easy access</span>
                </div>
              </label>
            </div>
          )}

          {/* Submit Button */}
          {step >= 3 && (
            <Button
              onClick={handleSubmit}
              disabled={submitting || rating === 0 || wouldMeetAgain === null}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl py-6 font-semibold"
              data-testid="submit-review-btn"
            >
              {submitting ? "Submitting..." : "Submit Feedback & Say Goodbye 👋"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeToSayGoodbye;
